import React, { useCallback, useEffect, useState } from 'react';
import BackButton from '../../../shared/components/BackButton';
import Title from './Title';
import { api } from '../../../shared/hooks/api';
import { error, success } from '../../../utils/toast';
import { useNavigate } from 'react-router-dom';
import { PageContentWrapper } from '../../../shared/styled/PageContentStyle';
import TabBar from './TabBar';
import useMergeState from '../../../shared/hooks/useMergeState';
import { WarningUnsavedChanges } from '../../../configs/warningProductModal';
import CancelModal from '../../../shared/components/CancelModal';

const initialContactPersonData = {
    name: '',
    phone: '',
    tier: 'tier 1',
    fax: '',
    tax_id: '',
    email: '',
    address: '',
    bank_account_name: '',
    bank_name: '',
    bank_account_number: '',
    is_internal: false,
    is_outsource_provider: false,
    payable_days: 0,
    contact_list: [],
    materials: [],
    outsource_materials: [],
};

const initialContactListData = [
    {
        id: 1,
        contact: {
            is_default: false,
            name: '',
            phone: '',
            email: '',
            position: '',
        },
    },
];

const CreateProvider = () => {
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [contactPerson, setContactPerson] = useMergeState(initialContactPersonData);
    const [contactList, setContactList] = useMergeState(initialContactListData);
    const [{ isCreating }, createProvider] = api.post('/materials/providers/');
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");

    useEffect(() => {
        if (contactPerson.name || 
            contactPerson.phone || 
            contactPerson.address || 
            contactPerson.fax ||
            contactPerson.email ||
            contactPerson.bank_account_name || 
            contactPerson.bank_name || 
            contactPerson.bank_account_number || 
            contactPerson.is_internal !== false || 
            contactPerson.is_outsource_provider !== false ||
            contactPerson.payable_days !== 0 || 
            contactList.some((contact: any) => 
                contact.contact.name || 
                contact.contact.phone || 
                contact.contact.email || 
                contact.contact.position
            ) ||
            contactPerson.materials.length > 0 ||
            contactPerson.outsource_materials.length > 0) {
            setIsModified(true);
        }
    }, [contactPerson, contactList]);

    // Add beforeunload event listener for browser navigation
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isModified) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const confirmNavigation = useCallback((path: string) => {
        if (isModified) {
            setNavigationPath(path);
            setShowUnsavedDialog(true);
            return false;
        }

        navigate(path);
        return true;
    }, [isModified, navigate]);

    const handleBackClick = () => {
        confirmNavigation("/providers/");
    };

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        await handleSubmit();
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    let nextId = Math.max(...contactList.map((contact: any) => contact.id));

    const handleChangePersonContact = (updatePersonContact: any) => {
        setIsModified(true);
        setContactPerson({
            ...contactPerson,
            name: updatePersonContact.name,
            provider_code: updatePersonContact.provider_code,
            phone: updatePersonContact.phone,
            fax: updatePersonContact.fax,
            email: updatePersonContact.email,
            address: updatePersonContact.address,
            tax_id: updatePersonContact.tax_id,
            bank_account_name: updatePersonContact.bank_account_name,
            bank_name: updatePersonContact.bank_name,
            bank_account_number: updatePersonContact.bank_account_number,
            is_internal: updatePersonContact.is_internal,
            is_outsource_provider: updatePersonContact.is_outsource_provider,
            payable_days: updatePersonContact.payable_days,
        });
    };

    const handleChangeContactList = (updateContact: any) => {
        setIsModified(true);
        setContactList(
            contactList.map((contact: any) => {
                if (contact.id === updateContact.id) {
                    return {
                        ...contact,
                        contact: {
                            ...contact.contact,
                            is_default: updateContact.is_default,
                            name: updateContact.name,
                            phone: updateContact.phone,
                            email: updateContact.email,
                            position: updateContact.position,
                        },
                    };
                } else if (updateContact.is_default) {
                    return {
                        ...contact,
                        contact: {
                            ...contact.contact,
                            is_default: false,
                        },
                    };
                }
                return contact;
            })
        );
    };

    const handleAddContact = () => {
        setIsModified(true);
        setContactList([
            ...contactList,
            {
                id: ++nextId,
                contact: {
                    is_default: false,
                    name: '',
                    phone: '',
                    email: '',
                    position: '',
                },
            },
        ]);
    };

    const handleSubmit = () => {
        const materials = contactPerson.materials.map((material: any) => ({
            ...material,
            material_id: material.id,
            price: material.providerPrice,
        }));
        const outsource_materials = contactPerson.outsource_materials.map((material: any) => ({
            ...material,
            material_id: material.id,
            price: material.providerPrice,
            note: material.note || "",
        }));

        const responseData = {
            ...contactPerson,
            is_internal: Boolean(contactPerson.is_internal),
            is_outsource_provider: Boolean(contactPerson.is_outsource_provider),
            contact_list: contactList.map(({ id, contact }: any) => {
                return {
                    id,
                    contact: {
                        ...contact,
                    },
                };
            }),
            materials: materials,
            outsource_materials: outsource_materials,
        };
        createProvider(responseData)
            .then((data: any) => {
                success('Thành Công', 'Tạo mới nhà cung cấp thành công');
                setIsModified(false);
                navigate(`/providers/${data.id}`);
                return true;
            })
            .catch((err: any) => {
                error('Lỗi tạo nhà cung cấp', err);
                return false;
            });
    };

    const handleDeleteContact = (contactId: number) => {
        setContactList(contactList.filter((contact: any) => contact.id !== contactId));
    };

    return (
        <PageContentWrapper>
            <BackButton label="Trở về" onClick={handleBackClick} />
            <Title setValidated={setValidated} submitForm={handleSubmit} isLoading={isCreating} />
            <TabBar
                contactList={contactList}
                contactPerson={contactPerson}
                mergeContactPerson={setContactPerson}
                onChangePersonContact={handleChangePersonContact}
                onChangeContactList={handleChangeContactList}
                onDeleteContactList={handleDeleteContact}
                onAddContactList={handleAddContact}
                validated={validated}
            />
            <CancelModal
                open={showUnsavedDialog}
                handleConfirm={handleSaveAndNavigate}
                handleClose={handleDontSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
                isLoading={isCreating}
            />
        </PageContentWrapper>

    );
};

export default CreateProvider;
